"use client";
import { cn } from "@scott/ui/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type React from "react";
import { useState } from "react";
import { Body } from "../body";
import { CloseIcon } from "../icon/icons/close-icon";
import { EyeIcon } from "../icon/icons/eye-icon";
import { EyeOffIcon } from "../icon/icons/eye-off-icon";
import { TickIcon } from "../icon/icons/tick-icon";
import { Label } from "../label";

const inputVariants = cva("", {
  variants: {
    status: {
      error: "",
      success: "",
      disabled: "disabled",
      readOnly: "read-only",
    },
    size: {
      default: "h-10 sm:h-12",
      sm: "h-[30px]",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  placeholder?: string;
  type: string;
  name: string;
  label?: string | React.ReactNode;
  hasIcon: boolean;
  message?: React.ReactNode;
  classNameInput?: string;
  disabled?: boolean;
  readOnly?: boolean;
  size?: "default" | "sm";
  isPassword?: boolean;
}

const Input = ({
  className,
  message,
  status,
  hasIcon,
  children,
  label,
  placeholder,
  type = "text",
  name,
  classNameInput,
  size,
  isPassword = false,
  ref,
  ...props
}: InputProps & {
  ref?:
    | React.RefObject<HTMLInputElement | null>
    | ((instance: HTMLInputElement | null) => void);
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const iconClass =
    size === "sm"
      ? "p-2 read-only:bg-neutral-light"
      : "p-4 read-only:bg-neutral-light";
  const inputType = isPassword ? (showPassword ? "text" : "password") : type;

  return (
    <div className={cn("flex w-full flex-1 flex-col gap-1")}>
      {label && (
        <Label
          className={cn(
            "py-1 font-bold text-sm",
            props.disabled && "text-neutral-medium",
            props.readOnly && "text-neutral-medium"
          )}
          htmlFor={name}
        >
          {label}
        </Label>
      )}
      <div
        className={cn(
          "peer flex border border-neutral-medium hover:border-neutral-dark read-only:hover:border-neutral-medium disabled:bg-neutral-light",
          status === "error" && "border-danger-medium",
          status === "success" && "border-success-medium",
          props.disabled && "bg-neutral-light",
          className
        )}
      >
        <input
          className={cn(
            "flex flex-1 basis-[100%] justify-self-start rounded-none px-3 py-1 text-neutral-darker ring-offset-background file:border-0 file:bg-transparent file:font-medium file:text-sm placeholder:text-neutral-medium read-only:cursor-not-allowed read-only:bg-neutral-light read-only:text-neutral-medium focus:outline-none disabled:cursor-not-allowed disabled:text-neutral-medium",
            size === "sm" ? "text-sm" : "text-md",
            inputVariants({ size }),
            classNameInput
          )}
          name={name}
          placeholder={placeholder}
          ref={ref}
          type={inputType}
          {...props}
        />
        {(hasIcon || isPassword) && (
          <div
            className={cn(
              "flex flex-1 flex-column basis-[5%] items-center justify-center px-3 text-neutral-dark sm:px-4",
              hasIcon ? iconClass : ""
            )}
          >
            {isPassword ? (
              <button
                className="h-4 w-4 text-gray-500"
                onClick={() => setShowPassword((prev) => !prev)}
                type="button"
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            ) : (
              children
            )}
          </div>
        )}
      </div>
      {status && (
        <div>
          {status === "error" && (
            <span className="flex items-center gap-1">
              <div className="flex h-3 w-3 items-center justify-center rounded-full bg-error p-1">
                <CloseIcon className="h-2 w-2 text-white" size="sm" />
              </div>
              <Body className="text-error" size="xs">
                {message}
              </Body>{" "}
            </span>
          )}
          {status === "success" && (
            <span className="flex items-center gap-1">
              <TickIcon className="h-3 w-3 text-button-success" size="sm" />
              <Body className="text-button-success" size="xs">
                {message}
              </Body>{" "}
            </span>
          )}
          {props.disabled && message && (
            <span className="flex gap-1 text-neutral-medium">
              <Body size="xs">{message}</Body>
            </span>
          )}
          {!(props.disabled || status) && message && (
            <span className="flex gap-1">
              <Body size="xs">{message}</Body>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
Input.displayName = "Input";

export { Input };
